import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowSquareOut as ExternalLink } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import confetti from "canvas-confetti";
import { RENEWAL_PAYMENT_REQUIRED } from "../config";
import { RelayLoader } from "../components/RelayLoader";
import { homeSection } from '../lib/marketingHome';

const API = process.env.REACT_APP_BACKEND_URL;

const RENEW_BASE_PRICE = 54;
const RENEW_STRIPE_LINK = "https://buy.stripe.com/9B6bJ15I1dMrgqg9UvcfK06";
const RENEW_PRICING = {
  fresh: { pct: 50, price: "$27", code: "FRESH50", label: "mid-race fresh start" },
  renew: { pct: 25, price: "$40.50", code: "RENEW25", label: "end-of-race renewal" },
};

const COLOR = {
  bg: "var(--pr-bg)",
  ink: "var(--pr-ink)",
  ink2: "var(--pr-ink2)",
  muted: "var(--pr-muted)",
  line: "var(--pr-line)",
  card: "var(--pr-card)",
  gold: "var(--pr-gold)",
  green: "var(--pr-green)",
};

const ModeCard = ({ active, onClick, emoji, title, desc, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left rounded-2xl p-4 border-2 transition-all"
    style={{
      borderColor: active ? COLOR.ink : COLOR.line,
      background: active ? "var(--pr-tint-ink)" : COLOR.card,
    }}
    data-testid={testId}
  >
    <p className="text-sm font-bold mb-0.5" style={{ color: COLOR.ink }}>{emoji} {title}</p>
    <p className="text-xs leading-relaxed" style={{ color: COLOR.ink2 }}>{desc}</p>
  </button>
);

export default function RenewalPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fresh = searchParams.get("fresh") === "1";
  const [view, setView] = useState("loading"); // loading | not_member | not_connected | form | building | done
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState("keep");
  const [startDate, setStartDate] = useState("");
  const [paydayDelay, setPaydayDelay] = useState("7");
  const [target, setTarget] = useState("");
  const [daysPerWeek, setDaysPerWeek] = useState("");
  const [workingWeeks, setWorkingWeeks] = useState("");
  const [result, setResult] = useState(null);
  const [paidAck, setPaidAck] = useState(false);
  const pricing = fresh ? RENEW_PRICING.fresh : RENEW_PRICING.renew;

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/caddy/renew/info`, { credentials: "include" });
        if (res.status === 401) { navigate("/login?next=/app/renew", { replace: true }); return; }
        if (res.status === 403) { setView("not_member"); return; }
        const data = await res.json();
        setInfo(data);
        setStartDate(data.suggested_start_date || "");
        if (data.payday_delay != null) setPaydayDelay(String(data.payday_delay));
        if (data.weekly_target != null) setTarget(String(data.weekly_target));
        if (data.days_per_week != null) setDaysPerWeek(String(data.days_per_week));
        if (data.working_weeks != null) setWorkingWeeks(String(data.working_weeks));
        setView(data.sheet_connected ? "form" : "not_connected");
      } catch {
        setView("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildSeason = async () => {
    if (!startDate) { toast.error("Pick a start date for your new relay race"); return; }
    setView("building");
    try {
      const body = { mode, start_date: startDate, payday_delay: parseInt(paydayDelay || "0", 10) };
      if (fresh) body.fresh = true;
      if (mode === "reset") {
        if (target) body.weekly_target = parseFloat(target);
        if (daysPerWeek) body.days_per_week = parseInt(daysPerWeek, 10);
        if (workingWeeks) body.working_weeks = parseInt(workingWeeks, 10);
      }
      const res = await fetch(`${API}/api/caddy/renew`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Couldn't build your new sheet, try again"); setView("form"); return; }
      setResult(data);
      setView("done");
      confetti({ particleCount: 160, spread: 85, origin: { y: 0.6 }, colors: ["#F5CB5C", "#3E8E4E", "#2A1E5C"] });
    } catch {
      toast.error("Network hiccup, try again.");
      setView("form");
    }
  };

  if (view === "error") return <CaddyErrorCard />;

  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pr-bg)" }}>
        <RelayLoader label="Loading your renewal…" />
      </div>
    );
  }

  if (view === "not_member") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--pr-bg)" }}>
        <div className="max-w-sm text-center" data-testid="renew-join-gate">
          <h1 className="text-2xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
            Relay race renewals ride with Payday Relay
          </h1>
          <Link to={homeSection('season-pass')} className="inline-block px-8 py-3 rounded-full font-bold" style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)" }}>
            See membership options →
          </Link>
        </div>
      </div>
    );
  }

  if (view === "not_connected") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--pr-bg)" }}>
        <div className="max-w-sm text-center" data-testid="renew-not-connected">
          <h1 className="text-2xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
            First, connect your Google Sheet
          </h1>
          <p className="text-sm leading-relaxed mb-6" style={{ color: COLOR.ink2 }}>
            A new 52-week relay race builds a fresh 52-relay sheet in your own Google Drive. Connect your Google account
            from your setup link first. Then come back here.
          </p>
          {info?.setup_link ? (
            <Link to={info.setup_link} className="inline-block px-8 py-3 rounded-full font-bold" style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)" }} data-testid="renew-setup-link">
              Open my setup →
            </Link>
          ) : (
            <Link to="/support" className="inline-block px-8 py-3 rounded-full font-bold" style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)" }}>
              Ask Caddy for help →
            </Link>
          )}
          <p className="text-xs mt-6">
            <Link to="/app" className="underline" style={{ color: COLOR.muted }}>← Back to Payday Relay</Link>
          </p>
        </div>
      </div>
    );
  }

  if (view === "building") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--pr-bg)" }}>
        <div className="text-center" data-testid="renew-building">
          <h1 className="text-2xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
            Building your new race sheet…
          </h1>
          <p className="text-sm" style={{ color: COLOR.ink2 }}>Fresh 52 relays, straight into your Google Drive. ~20 seconds.</p>
        </div>
      </div>
    );
  }

  if (view === "done") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--pr-bg)" }}>
        <div className="max-w-sm text-center space-y-5" data-testid="renew-done">
          <h1 className="text-3xl" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
            New race, new sheet
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: COLOR.ink2 }}>
            Your fresh 52-relay sheet is in your Google Drive and Payday Relay now syncs with it.
          </p>
          <a
            href={result?.sheet_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-1.5 w-full px-8 py-3.5 rounded-full font-bold transition-transform hover:scale-[1.02]"
            style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)" }}
            data-testid="renew-new-sheet-link"
          >
            Open my new sheet <ExternalLink className="w-4 h-4" />
          </a>
          {result?.archived_sheet_url && (
            <div className="rounded-2xl p-4 text-left border" style={{ background: "var(--pr-card)", borderColor: COLOR.line }} data-testid="renew-old-sheet-note">
              <p className="text-xs leading-relaxed" style={{ color: COLOR.ink2 }}>
                📚 <b style={{ color: COLOR.ink }}>Your old sheet stays in your Drive, untouched</b>, it's your permanent
                income record for the race, ready for your accounting or tax prep whenever you need it.{" "}
                <a href={result.archived_sheet_url} target="_blank" rel="noreferrer" className="underline" style={{ color: COLOR.ink }}>
                  Open the archive →
                </a>
              </p>
            </div>
          )}
          <Link to="/app" className="inline-block text-sm underline" style={{ color: COLOR.muted }} data-testid="renew-back-to-caddy">
            Back to Payday Relay →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--pr-bg)" }}>
      <header className="px-5 pt-5 pb-2">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-1.5 text-sm hover:opacity-70" style={{ color: COLOR.ink }} data-testid="renew-back-link">
            <ArrowLeft className="w-4 h-4" /> Payday Relay
          </Link>
          <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: COLOR.muted }}>
            {fresh ? "🔄 Fresh Start" : "🏁 New Relay Race"}
          </p>
        </div>
      </header>

      <main className="flex-1 px-5 py-6">
        <div className="max-w-md mx-auto space-y-5" data-testid="renew-form">
          <div className="text-center">
            <h1 className="text-3xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
              {fresh ? "Start a fresh relay" : "Roll into your next 52 relays"}
            </h1>
            <p className="text-sm leading-relaxed" style={{ color: COLOR.ink2 }}>
              We'll build a brand-new sheet in your Google Drive. Your current sheet stays exactly where it is, 
              a permanent record of this race for your books.
            </p>
          </div>

          {fresh && (
            <div className="rounded-2xl p-4 border text-xs leading-relaxed" style={{ background: "var(--pr-tint-red)", borderColor: "var(--pr-card-line)", color: COLOR.ink2 }} data-testid="renew-fresh-warning">
              ⚠️ <b style={{ color: COLOR.ink }}>Heads up:</b> this ends your current race and starts you back at
              Relay 1. Your streak, milestones, and in-app race history reset. Your old sheet is archived in your
              Drive. Nothing is deleted, but this can't be undone.
            </div>
          )}

          {!RENEWAL_PAYMENT_REQUIRED ? (
            <div className="rounded-2xl p-4 border text-xs leading-relaxed" style={{ background: "var(--pr-tint-gold)", borderColor: COLOR.gold, color: COLOR.ink2 }} data-testid="renew-pricing-fyi">
              🎁 <b style={{ color: COLOR.ink }}>Beta perk. This one's on the house.</b> Starting a new
              52-relay race is included while you're in the beta, and nothing renews automatically. You
              choose when.
            </div>
          ) : (
            <div className="rounded-2xl p-4 border-2 space-y-3" style={{ background: "var(--pr-tint-gold)", borderColor: COLOR.gold }} data-testid="renew-payment-card">
              <p className="text-sm font-bold" style={{ color: COLOR.ink }}>
                🏷️ A new race is a fresh purchase. Your {pricing.label} is {pricing.pct}% off: {pricing.price}
              </p>
              <p className="text-xs leading-relaxed" style={{ color: COLOR.ink2 }}>
                Grab the Payday Relay system below and apply your founding-member code{" "}
                <b style={{ color: COLOR.ink }}>{pricing.code}</b> at checkout to bring it to {pricing.price}. Then come
                back here to build your new sheet. Nothing renews automatically.
              </p>
              <a
                href={RENEW_STRIPE_LINK}
                target="_blank"
                rel="noreferrer"
                className="block w-full py-3 rounded-full font-bold text-sm text-center transition-transform hover:scale-[1.01]"
                style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)" }}
                data-testid="renew-payment-link"
              >
                Renew for {pricing.price} with code {pricing.code} →
              </a>
              <label className="flex items-start gap-2 text-xs cursor-pointer" style={{ color: COLOR.ink2 }}>
                <input
                  type="checkbox"
                  checked={paidAck}
                  onChange={(e) => setPaidAck(e.target.checked)}
                  className="mt-0.5 w-4 h-4"
                  data-testid="renew-paid-ack"
                />
                <span>I've completed my renewal purchase, build my new race sheet.</span>
              </label>
            </div>
          )}

          <div className="space-y-2.5">
            <ModeCard
              active={mode === "keep"}
              onClick={() => setMode("keep")}
              emoji="🏎️"
              title="Keep my pace"
              desc="Same weekly target, same rhythm. One click and the new race is ready."
              testId="renew-keep-mode"
            />
            <ModeCard
              active={mode === "reset"}
              onClick={() => setMode("reset")}
              emoji="🛠️"
              title="Reset my numbers"
              desc="Adjust your weekly target, work days, or weeks off before the new sheet builds."
              testId="renew-reset-mode"
            />
          </div>

          {mode === "reset" && (
            <div className="rounded-2xl p-5 lg:p-6 pr-card space-y-3" data-testid="renew-reset-fields">
              <div>
                <label className="block text-xs font-bold mb-1" style={{ color: COLOR.ink }}>Weekly target ($)</label>
                <input
                  type="number" inputMode="decimal" min="1" step="0.01"
                  value={target} onChange={(e) => setTarget(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border text-sm"
                  style={{ borderColor: COLOR.line, color: COLOR.ink }}
                  data-testid="renew-target-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold mb-1" style={{ color: COLOR.ink }}>Work days / week</label>
                  <input
                    type="number" inputMode="numeric" min="1" max="7"
                    value={daysPerWeek} onChange={(e) => setDaysPerWeek(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border text-sm"
                    style={{ borderColor: COLOR.line, color: COLOR.ink }}
                    data-testid="renew-days-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1" style={{ color: COLOR.ink }}>Working weeks / year</label>
                  <input
                    type="number" inputMode="numeric" min="1" max="52"
                    value={workingWeeks} onChange={(e) => setWorkingWeeks(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border text-sm"
                    style={{ borderColor: COLOR.line, color: COLOR.ink }}
                    data-testid="renew-weeks-input"
                  />
                </div>
              </div>
              <p className="text-[11px] leading-relaxed" style={{ color: COLOR.muted }}>
                Not sure what to pick? Test scenarios in the{" "}
                <Link to="/app/calculator" className="underline" style={{ color: COLOR.ink }}>Relay Calculator</Link> first.
              </p>
            </div>
          )}

          <div className="rounded-2xl p-5 lg:p-6 pr-card space-y-3">
            <div>
              <label className="block text-xs font-bold mb-1" style={{ color: COLOR.ink }}>New race starts</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border text-sm"
                style={{ borderColor: COLOR.line, color: COLOR.ink }}
                data-testid="renew-start-date"
              />
              {info?.season_end && (
                <p className="text-[11px] mt-1" style={{ color: COLOR.muted }}>
                  Your current relay race ends {info.season_end}. We suggest starting the day after.
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-bold mb-1" style={{ color: COLOR.ink }}>Payday rhythm (days)</label>
              <input
                type="number" inputMode="numeric" min="0" max="60"
                value={paydayDelay} onChange={(e) => setPaydayDelay(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border text-sm"
                style={{ borderColor: COLOR.line, color: COLOR.ink }}
                data-testid="renew-delay-input"
              />
            </div>
          </div>

          <button
            onClick={buildSeason}
            disabled={RENEWAL_PAYMENT_REQUIRED && !paidAck}
            className="w-full py-3.5 rounded-full font-bold text-base transition-transform hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
            style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
            data-testid="renew-build-btn"
          >
            Build my new race sheet 🏁
          </button>

          <p className="text-[11px] text-center leading-relaxed" style={{ color: COLOR.muted }} data-testid="renew-disclaimer">
            Your old sheet is never modified or deleted. It stays in your Drive as your record.
            Payday Relay is an educational tool. Not financial, tax, or investment advice.
          </p>
        </div>
      </main>
    </div>
  );
}
